import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { DataOptions } from "vuetify";
import { FILTER_NAMES } from "@/static/filterNames";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import {
  getBookingReportItems,
  postToggleReportItemStatus,
  postPairBookingReportItemsWithBookingItems,
  getBookingItems,
  postPairBookingReportItemWithBookingItem,
} from "@/requests/report/bookingReports";
import { BookingReportItem } from "@/models/api/report/BookingReportItem";
import { BookingItem } from "@/models/api/report/BookingItem";

export interface BookingReportsStateInterface {
  //ReportItems
  isLoading: boolean;
  reportItemTableData: Array<BookingReportItem>;
  reportItemTableCount: number;
  reportItemTableOptions: DataOptions;
  reportItemTableFilters: Array<CriterionItemRequest> | null;
  selectedReportItemId: number | null;
  selectedReportItem: BookingReportItem | null;

  //BookingItems
  bookingItemTableCount: number;
  bookingItemTableData: Array<BookingItem>;
  bookingItemTableOptions: DataOptions;
  bookingItemTableFilters: Array<CriterionItemRequest> | null;
  selectedBookingItemId: number | null;
  selectedBookingItem: BookingItem | null;
}

export const BookingReportsState: BookingReportsStateInterface = {
  isLoading: false,
  reportItemTableData: [],
  reportItemTableCount: -1,
  reportItemTableOptions: new DefaultDataOptions(),
  reportItemTableFilters: null,
  selectedReportItemId: null,
  selectedReportItem: null,

  bookingItemTableData: [],
  bookingItemTableCount: -1,
  bookingItemTableOptions: new DefaultDataOptions(),
  bookingItemTableFilters: null,
  selectedBookingItemId: null,
  selectedBookingItem: null,
};

const BookingReportsModule = defineModule({
  state: BookingReportsState as BookingReportsStateInterface,
  namespaced: true as const,
  modules: {},
  getters: {},
  mutations: {
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setReportItemTableData(state, param: Array<BookingReportItem>) {
      state.reportItemTableData = param;
    },
    setReportItemTableCount(state, param: number) {
      state.reportItemTableCount = param;
    },
    setReportItemTableOptions(state, param: DataOptions) {
      state.reportItemTableOptions = param;
    },
    setReportItemTableFilters(state, param: null | Array<CriterionItemRequest>) {
      state.reportItemTableFilters = param;
    },
    setSelectedReportItemId(state, param: number | null) {
      state.selectedReportItemId = param;
    },
    setSelectedReportItem(state, param: BookingReportItem | null) {
      state.selectedReportItem = param;
    },

    setBookingItemTableOptions(state, param: DataOptions) {
      state.bookingItemTableOptions = param;
    },
    setBookingItemTableCount(state, param: number) {
      state.bookingItemTableCount = param;
    },
    setBookingItemTableData(state, param: Array<BookingItem>) {
      state.bookingItemTableData = param;
    },
    setBookingItemTableFilters(state, param: null | Array<CriterionItemRequest>) {
      state.bookingItemTableFilters = param;
    },
  },
  actions: {
    async getReportItemTableData(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getBookingReportItems({
        count: state.reportItemTableOptions.itemsPerPage,
        page: state.reportItemTableOptions.page - 1,
        criterions: state.reportItemTableFilters,
        filterName: FILTER_NAMES.BookingReportItemStandardFilter,
      });
      commit.setReportItemTableCount(response.totalCount);
      commit.setReportItemTableData(response.items);
      commit.setIsLoading(false);
    },

    async toggleReportItemStatus(context, reportItemId: number) {
      const { dispatch } = _moduleActionContext(context);
      await postToggleReportItemStatus(reportItemId);
      dispatch.getReportItemTableData();
    },

    async pairBookingReportItemsWithBookingItems(context) {
      const { dispatch } = _moduleActionContext(context);
      await postPairBookingReportItemsWithBookingItems();
      await dispatch.getReportItemTableData();
    },

    async openSearchBookingItem(context, reportItem: BookingReportItem) {
      const { commit } = _moduleActionContext(context);
      commit.setSelectedReportItemId(reportItem.id);
      commit.setSelectedReportItem(reportItem);
    },

    async getBookingItemTableData(context) {
      const { commit, state } = _moduleActionContext(context);
      const response = await getBookingItems({
        count: state.bookingItemTableOptions.itemsPerPage,
        page: state.bookingItemTableOptions.page - 1,
        criterions: state.bookingItemTableFilters,
        filterName: FILTER_NAMES.BookingItemListStandardFilter,
      });
      commit.setBookingItemTableCount(response.totalCount);
      commit.setBookingItemTableData(response.items);
    },

    async pairBookingReportItemWithBookingItem(context, payload: { reportItemId: number; bookingItemId: number }) {
      const { dispatch } = _moduleActionContext(context);
      await postPairBookingReportItemWithBookingItem(payload.reportItemId, payload.bookingItemId);
      await dispatch.getReportItemTableData();
    },
  },
});
export default BookingReportsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, BookingReportsModule);